import React, { forwardRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import AddressSearchManualStep, {
  type AddressSearchManualStepHandle,
} from "../../../common/AddressSearchManualStep";

type Props = {
  onValidationChange?: (isValid: boolean) => void;
  onContinue?: () => void;
  testID?: string | undefined;
};

export type { AddressSearchManualStepHandle as SenderAddressStepHandle };

const SenderAddressStepNew = forwardRef<AddressSearchManualStepHandle, Props>(
  ({ onValidationChange, onContinue, testID }, ref) => {
    const { t } = useTranslation();

    const senderAddress =
      useSessionStore((s: SessionState) => (s as any).senderAddress) || "";
    const senderCity =
      useSessionStore((s: SessionState) => (s as any).senderCity) || "";
    const senderZipCode =
      useSessionStore((s: SessionState) => (s as any).senderZipCode) || "";
    const senderState =
      useSessionStore((s: SessionState) => (s as any).senderState) || "";
    const setSenderAddress = useSessionStore(
      (s: SessionState) => (s as any).setSenderAddress
    );
    const setSenderCity = useSessionStore(
      (s: SessionState) => (s as any).setSenderCity
    );
    const setSenderZipCode = useSessionStore(
      (s: SessionState) => (s as any).setSenderZipCode
    );
    const setSenderState = useSessionStore(
      (s: SessionState) => (s as any).setSenderState
    );

    const bindings = useMemo(
      () => ({
        address: senderAddress,
        city: senderCity,
        zipCode: senderZipCode,
        state: senderState,
        setAddress: (value: string) => setSenderAddress?.(value),
        setCity: (value: string) => setSenderCity?.(value),
        setZipCode: (value: string) => setSenderZipCode?.(value),
        setState: (value: string) => setSenderState?.(value),
      }),
      [
        senderAddress,
        senderCity,
        senderZipCode,
        senderState,
        setSenderAddress,
        setSenderCity,
        setSenderZipCode,
        setSenderState,
      ]
    );

    const labels = useMemo(
      () => ({
        line1: t("SENDER_ADDRESS_LINE1_PLACEHOLDER"),
        line2: t("SENDER_ADDRESS_LINE2_PLACEHOLDER"),
        city: t("SENDER_ADDRESS_CITY_PLACEHOLDER"),
        postal: t("SENDER_ADDRESS_POSTAL_PLACEHOLDER"),
        state: t("SENDER_ADDRESS_STATE_PLACEHOLDER"),
      }),
      [t]
    );

    const testIds = useMemo(
      () => ({
        search: "senderAddressSearch",
        suggestion: "senderAddressSuggestion",
        enterManually: "enterAddressManually",
        searchInstead: "searchAddressInstead",
        address: "senderAddress",
        address2: "senderAddress2",
        city: "senderCity",
        zip: "senderZipCode",
        state: "senderState",
        stateOption: "senderStateOption",
        continue: "continueButton",
      }),
      []
    );

    return (
      <AddressSearchManualStep
        ref={ref}
        testID={testID}
        bindings={bindings}
        labels={labels}
        testIds={testIds}
        onValidationChange={onValidationChange}
        onContinue={onContinue}
      />
    );
  }
);

SenderAddressStepNew.displayName = "SenderAddressStepNew";

export default SenderAddressStepNew;
